import type { UserDetails } from '@blinq-types';
import { LoginForm, useLoginForm } from '@components/LoginForm';
import type { LoginMode } from '@components/LoginForm/types';
import signupStyles from '@components/Onboarding/signup.module.scss';
import { analytics } from '@modules/analytics';
import { Box, Heading, Stack } from '@ui-lib';
import { rebrandTheme } from '@ui-lib/css';
import { AnimatePresence } from 'motion/react';
import { useCallback } from 'react';

import { Logo } from '../components/Logo';
import { LeftPanel } from '../components/Panels';
import { BusinessOnboardingLayout } from '../layout';

export function Content({
  presetEmail,
  usePopupAuth,
}: {
  presetEmail?: string;
  usePopupAuth?: boolean;
} = {}) {
  const loginFormProps = useLoginForm(presetEmail);

  const showHeading = !(loginFormProps?.enteredPersonalEmail && !loginFormProps?.favouredEmail);

  const handleLogin = useCallback(
    (_userDetails: UserDetails, mode: LoginMode) => {
      if (mode === 'signup') {
        analytics.track({
          name: 'businessonboarding_accountcreated',
        });
      }

      if (loginFormProps.favouredEmail === 'work') {
        analytics.track({
          name: 'businessonboarding_switchingtoworkemail',
        });
      }

      if (loginFormProps.favouredEmail === 'personal') {
        analytics.track({
          name: 'businessonboarding_continuedwithpersonalemail',
        });
      }
    },

    [loginFormProps.favouredEmail],
  );

  return (
    <>
      <LeftPanel>
        <Box
          flexGrow={1}
          padding={{
            mobile: 'medium',
            tablet: 'large',
          }}
        >
          <Logo />
          <AnimatePresence>
            <Box
              height="full"
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexGrow={1}
              paddingTop={{
                mobile: 'xlarge',
              }}
            >
              <Box
                flexGrow={1}
                style={{
                  maxWidth: '480px',
                }}
              >
                <Stack gap="xsmall">
                  {showHeading && <Heading level="3">Create an account or login</Heading>}
                  <LoginForm
                    {...loginFormProps}
                    mode="hybrid"
                    preferWorkEmail={true}
                    onLogin={handleLogin}
                    usePopupAuth={usePopupAuth}
                  />
                </Stack>
              </Box>
            </Box>
          </AnimatePresence>
        </Box>
      </LeftPanel>
      <Box
        className={rebrandTheme}
        position="relative"
        width="full"
        overflow="hidden"
        background="cream"
        display={{ desktop: 'block', tablet: 'none', mobile: 'none' }}
        aria-hidden
      >
        <img
          className={signupStyles['side-illustration']}
          src="/img/onboarding/rebrand/signup-side-illustration.png"
          alt=""
        />
      </Box>
    </>
  );
}

export const SignUp = (): JSX.Element => {
  return (
    <BusinessOnboardingLayout pageTitle="Sign up">
      <Content />
    </BusinessOnboardingLayout>
  );
};
